import { ErrorBoundary } from 'components/ErrorBoundary';
import { ContainerState } from 'containers/MainLayout/types';
import React, { FC, useMemo } from 'react';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { Category } from 'types/schema';
import generateProductSlug from 'utils/generateProductSlug';
import { createSearchQueryfromSlug, getPathSegmentFromSlug } from 'utils/getPathSegmentFromSlug';
import getSelectedCategory from 'utils/getSelectedCategory';
import media from 'utils/mediaStyle';
import { pathnameChecker } from 'utils/url';
import { generateSlugFromID, getPropertyFromCategory } from 'utils/urls/categoriesPathSegment';

const Label = styled.div`
  text-align: center;
  font-size: 14px;
  flex: 1;
  font-weight: 400;
  white-space: nowrap;
`;

const LabelWrapper = styled.div`
  min-width: 100%;
  box-sizing: border-box;
  white-space: nowrap;
  text-align: center;
  padding: 8px 12px;
  ${media.lg`
    display: flex;
  `};
`;

const CategoryElment = styled.div`
  position: relative;
  background: #fcfffc;
`;
const SubCategory = styled.div<any>`
  pointer-events: none;
  position: absolute;
  transition: opacity 0.3s ease-in-out;
  left: 100%;
  z-index: 1;
  border: 1px solid #dce0e9;
  background: #fcfffc;
  opacity: 0;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  box-shadow: 4px 4px 8px rgba(4, 15, 15, 0.12);
  top: ${({ bottom }) => (bottom ? 'auto' : '-1px')};
  bottom: ${({ bottom }) => (bottom ? '-1px' : 'auto')};
  min-width: 134pt;
  padding: 6pt;
`;

const CategoryNavContainer = styled.nav`
  padding-right: 5px;
  border-collapse: collapse;
  display: inline-flex;
  flex-direction: row;
  justify-content: center;
  .ant-menu-horizontal {
    border-bottom: none;
  }
  ${CategoryElment} + ${CategoryElment} {
    border-top: 1px solid #dce0e9;
  }
  ${CategoryElment}:last-child {
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;
  }
  ${CategoryElment}:first-child {
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
  }
  ${CategoryElment}:hover ${SubCategory} {
    pointer-events: initial;
    opacity: 1;
  }
`;

const CategoryWrapper = styled.div<any>`
  overflow: auto;
  z-index: 1;
  display: flex;
  transition: left 0.3s ease-in-out;
`;

const Menu = styled.div<any>`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  width: 100%;
  gap: 12px;
  padding-bottom: 10px;
  padding-top: 8px;
  @media (max-width: 767px) {
    padding-left: 10pt;
  }
`;

const MenuItem = styled.div`
  color: rgba(0, 0, 0, 0.85);
  transition: all 0.3s ease;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  &.selected {
    color: #43a047;
    border-color: #43a047;
    a div div {
      color: #43a047;
    }
  }
  a {
    color: inherit;
  }
`;

const CategoryContainer = styled.div`
  flex-shrink: 0;
  z-index: 0;
  overflow-x: hidden;
  display: none;
  ${media.lg`
    display: block;
    margin-bottom: 24px;
  `};
`;

interface CategoryNavProps extends RouteComponentProps {
  main?: ContainerState;
}

const SubCategoryNavBarDesktop: FC<CategoryNavProps> = (props) => {
  const { main, location } = props;
  const lang = localStorage.getItem('lang') || 'vi';
  const path = pathnameChecker.isFavoritePage(location.pathname) ? '/favorite' : '';
  const segment = getPathSegmentFromSlug(location);
  const searchQuery = createSearchQueryfromSlug(location, segment);
  const region = main?.buyer?.region ? main?.buyer?.region : localStorage.getItem('region');

  const selectedCategory = useMemo(() => {
    const substate = main;
    return getSelectedCategory(
      searchQuery,
      substate && (path === '/favorite' ? substate.favoriteCategories : substate.categories),
    );
  }, [searchQuery, main?.favoriteCategories, main?.categories]);
  const currentCategories = useMemo(() => {
    if (selectedCategory) {
      return [
        {
          name: 'All',
          nameLocal: lang === 'en' ? 'All' : 'Tất cả',
          children: [],
        } as Category,
      ].concat(selectedCategory.children || (selectedCategory.parent && selectedCategory.parent.children));
    }
    return [];
  }, [selectedCategory]);
  return (
    <ErrorBoundary>
      <CategoryContainer>
        {selectedCategory && (
          <CategoryWrapper>
            <CategoryNavContainer>
              <Menu>
                {currentCategories.map((category: Category, index: number) => {
                  if (category.id === 81 && region === 'HCM') return null;
                  if (index === 0 && selectedCategory) {
                    const targetCategory = {
                      categoryName: getPropertyFromCategory(selectedCategory, 'name'),
                      slug: generateProductSlug(
                        getPropertyFromCategory(selectedCategory, 'nameLocal'),
                        `c${getPropertyFromCategory(selectedCategory, 'id')}`,
                        lang,
                      ),
                    };
                    const isSelected =
                      (selectedCategory && selectedCategory.parent) || searchQuery.indexOf('tags') > -1;
                    return (
                      <MenuItem key={index} className={isSelected ? '' : 'selected'}>
                        <Link
                          to={
                            path === '/favorite'
                              ? `${path}${
                                  selectedCategory ? `?category=${encodeURIComponent(targetCategory.categoryName)}` : ''
                                }`
                              : `/${targetCategory.slug}`
                          }
                        >
                          <LabelWrapper>
                            <Label>{category.nameLocal}</Label>
                          </LabelWrapper>
                        </Link>
                      </MenuItem>
                    );
                  }
                  let menuItemClassName = '';
                  if (selectedCategory && category && selectedCategory.name.indexOf(category.name) > -1) {
                    menuItemClassName = 'selected';
                  }
                  return (
                    <MenuItem key={index} className={menuItemClassName}>
                      <Link
                        to={
                          path === '/favorite'
                            ? `${path}?category=${encodeURIComponent(category.name)}`
                            : `/${generateSlugFromID(
                                {
                                  ...category,
                                  parent: category.parent || selectedCategory.parent,
                                },
                                lang,
                              )}`
                        }
                      >
                        <LabelWrapper>
                          <Label>{category?.nameLocal}</Label>
                        </LabelWrapper>
                      </Link>
                    </MenuItem>
                  );
                })}
              </Menu>
            </CategoryNavContainer>
          </CategoryWrapper>
        )}
      </CategoryContainer>
    </ErrorBoundary>
  );
};

export default withRouter(SubCategoryNavBarDesktop);
